/* eslint-disable i18next/no-literal-string */
"use client";
import type { ChatMessage } from "@ui/components/heroes/HeroConversationalAI/ChatAnimation";

import { Image } from "@ui/components/core/Image";
import clsx from "clsx";
import * as m from "motion/react-m";
import { useEffect, useRef, useState } from "react";
export function ChatHistory({
  userMessages,
  currentStep,
  isLoading,
  embeddedInBlock = false,
}: {
  currentStep: number;
  embeddedInBlock?: boolean;
  isLoading: boolean;
  userMessages: ChatMessage[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [cumulativeHeight, setCumulativeHeight] = useState(0);
  const [delayedUserMessages, setDelayedUserMessages] = useState<ChatMessage[]>([]);
  const [previousUserMessageCount, setPreviousUserMessageCount] = useState(0);

  // Delay showing new messages when userMessages increases
  useEffect(() => {
    if (userMessages.length > previousUserMessageCount) {
      // New message(s) added - delay showing them
      const timer = setTimeout(
        () => {
          setDelayedUserMessages(userMessages);
          setPreviousUserMessageCount(userMessages.length);
        },
        isLoading ? 2000 : 300,
      ); // Use loading duration if loading, otherwise short delay

      return () => clearTimeout(timer);
    } else if (userMessages.length < previousUserMessageCount) {
      // Messages removed (unlikely but handle it)
      setDelayedUserMessages(userMessages);
      setPreviousUserMessageCount(userMessages.length);
    }
  }, [userMessages, isLoading, previousUserMessageCount]);

  // Create a default message to show initially
  const defaultMessage = {
    id: "default-message",
    content: "Minimal techno song with a dynamic beat",
    type: "user" as const,
  };

  // Combine default message with delayed user messages
  const allMessages = [defaultMessage, ...delayedUserMessages];

  // Calculate cumulative height based on delayedUserMessages (not immediate userMessages)
  useEffect(() => {
    if (delayedUserMessages.length <= 1) {
      setCumulativeHeight(0);
      return;
    }

    // Base sliding on delayed messages count
    const messagesToHide = Math.max(0, delayedUserMessages.length - 1);

    let totalHeight = 0;

    for (let i = 0; i < messagesToHide; i++) {
      if (messageRefs.current[i]) {
        const height = messageRefs.current[i]!.offsetHeight;
        totalHeight += height;
      }
    }

    setCumulativeHeight(totalHeight);
  }, [allMessages, currentStep, delayedUserMessages, isLoading]);

  // Auto-scroll to the latest message
  useEffect(() => {
    if (containerRef.current && allMessages.length > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [allMessages]);

  return (
    <div className='relative h-full overflow-hidden'>
      <m.div
        animate={{
          opacity: 1,
          y: -cumulativeHeight,
          // Move up by cumulative height of hidden messages
        }}
        className='scrollbar-none -mt-4 flex h-full flex-col overflow-y-auto overflow-x-hidden pl-10'
        initial={{ opacity: 0, y: 0 }}
        ref={containerRef}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
      >
        {allMessages.map((message, index) => (
          <m.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            key={message.id}
            ref={el => {
              messageRefs.current[index] = el;
            }} // Store ref for each message
            style={{
              opacity: index === allMessages.length - 1 ? 1 : 0.6,
            }}
            transition={{
              duration: 0.4,
              delay: index * 0.1,
              ease: "easeOut",
            }}
          >
            <div
              className={clsx(
                "h-40 border-l md:h-52",
                embeddedInBlock ? "border-overlay-dark" : "border-overlay-darker",
              )}
            />
            <div
              className={clsx(
                "relative flex flex-col gap-8 pl-16",
                index !== allMessages.length - 1 &&
                  (embeddedInBlock ? "border-l border-overlay-dark" : "border-l border-overlay-darker"),
              )}
            >
              <div className='absolute left-0 top-0 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full bg-[#C4C2C1]'>
                <div className='h-4 w-4 rounded-full bg-dark-new' />
              </div>
              <div className='-mt-3 flex flex-col gap-4'>
                <p className='f-paragraph-04 text-overlay-darker-plus md:f-paragraph-03'>
                  {allMessages.length - index} mins ago
                </p>
                <p className='f-paragraph-04 text-overlay-darker-plus md:f-paragraph-03'>{message.content as string}</p>
              </div>
              <Image
                className='h-80 object-contain object-left'
                height={96}
                src='/assets/images/music/landing/variation.svg'
                width={401}
              />
            </div>
          </m.div>
        ))}
      </m.div>
    </div>
  );
}
